import { type FC } from "react";
import { Navigate, generatePath, useMatch, useParams } from "react-router-dom";

import { useRecoilValue } from "recoil";

import { useShouldShowWebApis } from "../../apis/hooks";
import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId } from "../../gql-generated/types";
import { PageContentSpinner } from "../../layout/components/PageContentSpinner";
import { useIsInitialized } from "../../shared/hooks/useIsInitialized";
import { useOrganizationsV2 } from "../../shared/hooks/useOrganizationsV2";
import { AppLayout } from "../components/AppLayout";
import {
    defaultOrganizationContainerSelector,
    organizationsLoadingState,
    organizationsSelector,
} from "../recoil/organizations";

import { AbsoluteRoute } from "./absoluteRoutes";

export const AppRoutes: FC<{ children: React.ReactNode }> = ({ children }) => {
    const { loading: isFeatureGatesLoading, configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const defaultOrgV1 = useRecoilValue(defaultOrganizationContainerSelector);
    const { organizationContainers: organizationContainersV1 } = useRecoilValue(organizationsSelector);
    const orgsLoadingV1 = useRecoilValue(organizationsLoadingState);
    const { data: shouldShowWebApis, loading: shouldShowWebApisLoading } = useShouldShowWebApis();
    const isInitialized = useIsInitialized();
    const {
        defaultOrganizationContainer: defaultOrgV2,
        loading: isOrgsLoadingV2,
        organizations: organizationContainersV2,
    } = useOrganizationsV2();

    const { orgId } = useParams();

    const matchAPIS = useMatch({ path: AbsoluteRoute.API, end: false });
    const matchRoot = useMatch({ path: AbsoluteRoute.ROOT, end: true });
    const matchWelcome = useMatch({ path: AbsoluteRoute.WELCOME, end: false });
    const matchCameraKit = useMatch({ path: AbsoluteRoute.ORG_CAMERA_KIT, end: false });

    const orgsLoading = isMyLensesOrgsV2Enabled ? isOrgsLoadingV2 : orgsLoadingV1;

    // TODO: Drop isFeatureGatesLoading here — GatedRoute now waits on COF itself,
    // so this subtree-wide block is redundant for gated routes.
    if (orgsLoading || shouldShowWebApisLoading || isFeatureGatesLoading || !isInitialized) {
        return <PageContentSpinner />;
    }

    const defaultOrg = isMyLensesOrgsV2Enabled ? defaultOrgV2 : defaultOrgV1;
    const organizationContainers = isMyLensesOrgsV2Enabled ? organizationContainersV2 : organizationContainersV1;
    const isValidOrg = organizationContainers.filter((org) => org.id === orgId);

    if (orgId !== undefined && isValidOrg.length === 0) {
        if (!matchWelcome && !matchCameraKit) {
            // TODO Only keep the ORG id when coming from CameraKit, i.e. not Lenses, etc.
            return (
                <Navigate
                    to={generatePath(AbsoluteRoute.WELCOME_ORG, {
                        orgId,
                    })}
                />
            );
        }
        return <AppLayout>{children}</AppLayout>;
    }

    if (
        (!shouldShowWebApis && !!matchAPIS) ||
        (isValidOrg.length === 0 && !!defaultOrg && !!orgId) ||
        (defaultOrg && !!matchWelcome)
    ) {
        return <Navigate to={AbsoluteRoute.ROOT} />;
    }

    if (matchRoot) {
        return <Navigate to={AbsoluteRoute.HOME} />;
    }

    return <AppLayout>{children}</AppLayout>;
};
